import React, { useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAuth0 } from "@auth0/auth0-react";
import { companyLogoUrl } from "../../../../apis/company-logo-apis/companyLogoApis";
import { LOGO_ACCEPT_ATTRIBUTE } from "../../data/uploadRules";

// A branding image (hero banner, favicon). Uploaded as soon as it is chosen;
// the path is saved with the rest of the Overview (and goes live on Publish
// changes).
// `name`: the field's label ("Hero image"); `upload(file, tokens)` returns
// { [pathKey]: path }; `validate(file)` says why a file cannot be used;
// `previewSize`: { width, height } of the preview box; `error`: a problem
// the form found (shown instead of the hint).
const BrandImageField = ({
  name,
  path,
  pathKey,
  upload,
  validate,
  hint,
  icon: Icon,
  previewSize = { width: 168, height: 64 },
  accept = LOGO_ACCEPT_ATTRIBUTE,
  error: formError,
  onChange,
  onUploading,
}) => {
  const theme = useTheme();
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const url = companyLogoUrl(path);

  const handleFile = async (file) => {
    if (!file) return;
    const problem = validate(file);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    onUploading?.(true);
    setError("");
    try {
      const accessToken = await getAccessTokenSilently();
      const idToken = (await getIdTokenClaims()).__raw;
      const result = await upload(file, { accessToken, idToken });
      onChange(result[pathKey]);
    } catch (e) {
      setError(e.message || "Could not upload the image. Try again.");
    } finally {
      setBusy(false);
      onUploading?.(false);
    }
  };

  return (
    <Box>
      <Typography
        component="span"
        sx={{
          display: "block",
          fontSize: "0.875rem",
          fontWeight: 600,
          color: theme.customColors.text.strong,
          mb: 0.75,
        }}
      >
        {name}
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
      >
        <Box
          sx={{
            ...previewSize,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            border: `1px dashed ${theme.customColors.border.dashed}`,
            backgroundColor: theme.customColors.background.infoBox,
            overflow: "hidden",
          }}
        >
          {url ? (
            <Box
              component="img"
              src={url}
              alt={`${name} preview`}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            Icon && <Icon sx={{ color: theme.customColors.icons.secondary }} />
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1}>
            <Button
              component="label"
              variant="outlined"
              size="small"
              disabled={busy}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              {busy ? "Uploading…" : url ? "Replace image" : "Upload image"}
              <input
                hidden
                type="file"
                aria-label={`Upload ${name.toLowerCase()}`}
                accept={accept}
                onChange={(e) => {
                  handleFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </Button>
            {url && (
              <Button
                size="small"
                disabled={busy}
                onClick={() => {
                  setError("");
                  onChange("");
                }}
                sx={{ textTransform: "none" }}
              >
                Remove
              </Button>
            )}
          </Stack>
          <Typography
            variant="caption"
            role={error || formError ? "alert" : undefined}
            sx={{
              display: "block",
              mt: 0.5,
              color:
                error || formError
                  ? theme.customColors.status.error.text
                  : theme.customColors.text.secondary,
            }}
          >
            {error || formError || hint}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default BrandImageField;
